/**
 * Der Rechenweg als Erklärung, nicht als Ergebnis.
 *
 * Die Tabelle links sagt, WAS herauskommt. Diese Spalte sagt, WIE — Schritt
 * für Schritt, in der Reihenfolge und mit den Formeln des
 * Kalkulationsmodells der Mühle. Jeder Schritt zeigt die Rechnung mit den
 * Zahlen, die gerade gelten, und darunter den Satz aus dem Modell, aus dem
 * die Regel stammt.
 *
 * Das ist Absicht: Wer einem Kunden einen Preis nennt, muss ihn begründen
 * können. Eine Zahl ohne Herleitung ist im Verkaufsgespräch wertlos.
 *
 * Gerechnet wird hier NICHT neu — bis auf die Empfindlichkeiten, die reine
 * Was-wäre-wenn-Vergleiche sind und den serverseitig ermittelten Preis nicht
 * berühren.
 */

import { berechneMehlAbHof, KONTRAKT_TEXT, type Kontraktmonat } from "../shared/muehle.js";
import { formatProzent, formatTonnen, formatVorzeichen, roundDiv } from "../shared/money.js";
import { MONATSNAMEN } from "../shared/datum.js";
import type { PreisfindungStamm } from "../shared/types.js";
import type { PreisZeile } from "./pages/Preisfindung.js";

/*
 * Zwei Nachkommastellen, nicht vier.
 *
 * formatE4 zeigt bis zu vier — richtig für die Stammdatenpflege, falsch
 * hier: „64,1026 €/t" liest sich niemand als Betrag. Diese Spalte soll
 * verstanden werden, nicht abgetippt; die genauen Werte stehen links in der
 * Tabelle.
 */
const t = (e4: number) =>
  (e4 / 10_000).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** e4 → "28,0" — für Verbrauch und Dieselpreis, wo Nachkommastellen zählen. */
const mitStellen = (e4: number, stellen: number) =>
  (e4 / 10_000).toLocaleString("de-DE", {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });

// Vorzeichen kommt aus shared/money.ts — dieselbe Funktion wie in der Tabelle.
const vorzeichen = formatVorzeichen;

function Schritt({
  nr,
  titel,
  quelle,
  warnung,
  children,
}: {
  nr: number;
  titel: string;
  /** Der Satz aus dem Kalkulationsmodell, auf dem der Schritt beruht. */
  quelle: React.ReactNode;
  warnung?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <section className="pf-schritt">
      <h4>
        <span className="pf-nr">{nr}</span>
        {titel}
      </h4>
      {children && <div className="rechnung">{children}</div>}
      <p className={warnung ? "pf-quelle pf-warnung" : "pf-quelle"}>{quelle}</p>
    </section>
  );
}

function Zeile({
  bez,
  wert,
  art,
}: {
  bez: React.ReactNode;
  wert: React.ReactNode;
  art?: "summe" | "gesamt" | "abzug";
}) {
  return (
    <div className={`rechnung-zeile${art === "abzug" ? "" : art ? ` ${art}` : ""}`}>
      <span className="bez">{bez}</span>
      <span className={art === "abzug" ? "abschlag" : undefined}>{wert}</span>
    </div>
  );
}

/**
 * Was der Rechenweg im Kontraktmodus zusaetzlich zeigen muss.
 *
 * Die Schritte 1–6 bleiben unveraendert und gelten fuer EINEN Monat der
 * Laufzeit — den Vertretermonat. Schritt 7 setzt sie in den Zusammenhang:
 * Er nennt den Monat ausdruecklich, zeigt die Gewichtung und sagt, was
 * NICHT ueber die Terminkurve laeuft.
 */
export interface Laufzeitbezug {
  monat: number;
  jahr: number;
  anzahl: number;
  guenstigster_e4: number;
  teuerster_e4: number;
  preis_je_t_e4: number;
  preis_je_100kg_e4: number;
  kontrakte: string[];
}

export function Rechenwegtafel({
  stamm,
  bezug,
  kontrakt,
  kontraktJahr,
  mitFracht,
  laufzeit = null,
}: {
  stamm: PreisfindungStamm;
  /** Der Artikel, an dem der Weg gezeigt wird. Null, solange nichts rechnet. */
  bezug: PreisZeile | null;
  kontrakt: Kontraktmonat;
  /** Jahr des Kontrakts — seit den datierten Codes Teil der Beschriftung. */
  kontraktJahr: number;
  mitFracht: boolean;
  /** Gesetzt im Kontraktmodus; sonst null. */
  laufzeit?: Laufzeitbezug | null;
}) {
  const p = stamm.parameter;
  const m = bezug?.ergebnis.mehl ?? null;
  const f = bezug?.ergebnis.fracht ?? null;
  const dieselE4 = stamm.notierungen.diesel?.wert_e4 ?? null;

  return (
    <div className="karte pf-tafel">
      <h2>Rechenweg</h2>
      <p className="hinweistext" style={{ marginTop: "-0.4rem" }}>
        {bezug ? (
          <>
            Gezeigt an <strong>{bezug.bezeichnung}</strong>
            {laufzeit && (
              <>
                , Liefermonat{" "}
                <strong>
                  {MONATSNAMEN[laufzeit.monat - 1]} {laufzeit.jahr}
                </strong>{" "}
                — einer von {laufzeit.anzahl}
              </>
            )}
            . Eine andere Zeile in der Tabelle anklicken wechselt den Artikel.
          </>
        ) : (
          "Sobald ein Szenario rechnet, stehen hier die Zahlen dazu."
        )}
      </p>

      <Schritt
        nr={1}
        titel="Getreidemischung"
        quelle={
          <>
            „Wz-Preis: abhängig von Qualität und Laufzeit des Angebotes." Maßgeblich ist der
            <strong> Liefermonat</strong>, nicht der Tag des Angebots: Q1 leitet sich vom März ab,
            Q2 und die Monate 6 und 7 vom Mai, der August vom Dezember mit Abschlag, Q4 vom
            Dezember. Die Prämie je Qualität kommt oben drauf — sie darf negativ sein, weil MATIF
            Rouen liefert und niedersächsischer Weizen sich den Weg zum Hafen erst verdienen muss.
          </>
        }
      >
        {m && bezug ? (
          <>
            {m.bestandteile.map((b) => {
              const name =
                bezug.mischung.find((x) => x.sorte === b.sorte)?.bezeichnung ?? b.sorte;
              const je = b.notierung_e4 - b.abschlag_e4 + b.praemie_e4;
              return (
                <Zeile
                  key={b.sorte}
                  bez={
                    <>
                      {formatProzent(b.anteil_bp)} % {name}
                      <span className="pf-teilrechnung">
                        {" "}
                        {t(b.notierung_e4)}
                        {/*
                          Beide über formatVorzeichen — der Abschlag wird
                          abgezogen, kann aber selbst negativ sein (inverser
                          Markt). Ein festes Minus schriebe dann doppelt.
                        */}
                        {b.abschlag_e4 !== 0 && ` ${formatVorzeichen(-b.abschlag_e4)}`}
                        {b.praemie_e4 !== 0 && ` ${formatVorzeichen(b.praemie_e4)}`}
                      </span>
                    </>
                  }
                  wert={`${t(je)} €/t`}
                />
              );
            })}
            <Zeile bez="Rohstoff gewichtet" wert={`${t(m.rohstoff_e4)} €/t Korn`} art="summe" />
          </>
        ) : (
          <Zeile bez={`MATIF ${KONTRAKT_TEXT[kontrakt]} ${kontraktJahr}`} wert="—" />
        )}
      </Schritt>

      <Schritt
        nr={2}
        titel="Mahllohn"
        warnung
        quelle={
          <>
            „Mahllohn: z. B. 50 EURO/to pro Wz/to" — <strong>je Tonne Weizen</strong>. Er wird in
            Schritt 4 mitdividiert und wirkt deshalb stärker, als er dasteht:{" "}
            {m && bezug
              ? `${t(m.mahllohn_e4)} €/t Weizen sind ${t(
                  roundDiv(m.mahllohn_e4 * 10_000, bezug.ausbeute_bp),
                )} €/t Mehl.`
              : "aus 50 €/t Weizen werden bei 78 % Ausbeute 64,10 €/t Mehl."}{" "}
            Wer ihn als „je Tonne Mehl" liest, liegt um rund ein Viertel daneben.
          </>
        }
      >
        <Zeile
          bez="je Tonne Weizen"
          wert={m ? `+${t(m.mahllohn_e4)}` : `+${t(p.mahllohn_e4 ?? 0)}`}
        />
      </Schritt>

      <Schritt
        nr={3}
        titel="Nachprodukte"
        quelle={
          <>
            „Diese werden je nach Ausbeute in der Kalkulation abgezogen. Z. B. bei einer Ausbeute
            von 78 % werden 22 % Nachprodukterlös 135 €/to, also 29,70 €/to."
          </>
        }
      >
        <Zeile
          bez={
            bezug
              ? `${formatProzent(bezug.nachprodukt_anteil_bp)} % × ${t(p.nachprodukt_preis_e4 ?? 0)} €/t`
              : "Anteil × Erlös"
          }
          wert={m ? `−${t(m.nachprodukt_erloes_e4)}` : "—"}
          art="abzug"
        />
        <Zeile bez="je Tonne Weizen" wert={m ? t(m.wz_bereinigt_e4) : "—"} art="summe" />
      </Schritt>

      <Schritt
        nr={4}
        titel="Umrechnung auf die Ausbeute"
        quelle={
          <>
            „Der Wz-Preis wird auf die Ausbeute 78 % umgerechnet. /0,78 = Mehlpreis ab Hof."
            Aus einer Tonne Weizen werden keine Tonne Mehl — die Kosten verteilen sich auf die
            geringere Menge.
          </>
        }
      >
        <Zeile
          bez={bezug ? `÷ Ausbeute ${formatProzent(bezug.ausbeute_bp)} %` : "÷ Ausbeute"}
          wert={m ? `${t(m.ab_hof_e4)} €/t Mehl` : "—"}
          art="summe"
        />
      </Schritt>

      <Schritt
        nr={5}
        titel="Befrachtung"
        quelle={
          mitFracht ? (
            <>
              „Fixkosten Personal 228,– € täglich, Fixkosten LKW 200,– € täglich. Variable Kosten
              errechnen sich aus Dieselpreis und Entfernung." Entscheidend:{" "}
              <strong>Das ist ein Tourpreis.</strong> Dieselbe Fahrt kostet bei halber Ladung je
              Tonne das Doppelte — deshalb steht die Lademenge in der Rechnung.
            </>
          ) : (
            <>
              „Befrachtung: Frachtpreis je nach Entfernung pro to addieren." Bei Selbstabholung
              entfällt sie — der Preis gilt dann ab Hof.
            </>
          )
        }
      >
        {f ? (
          <>
            <Zeile bez="Personal + LKW, fix je Tag" wert={t(f.personal_e4 + f.lkw_e4)} />
            <Zeile
              bez={
                dieselE4 !== null
                  ? `${f.strecke_km} km ÷ 100 × ${mitStellen(p.verbrauch_e4 ?? 0, 1)} l × ${mitStellen(dieselE4, 3)} €`
                  : "Kraftstoff"
              }
              wert={t(f.kraftstoff_e4)}
            />
            <Zeile
              bez={`${f.strecke_km} km × ${mitStellen(p.maut_e4_km ?? 0, 3)} €`}
              wert={t(f.maut_e4)}
            />
            <Zeile bez="Tour hin und zurück" wert={t(f.tour_e4)} art="summe" />
            <Zeile
              bez={`÷ ${formatTonnen(f.lademenge_kg)} t Lademenge`}
              wert={`${t(f.je_tonne_e4)} €/t`}
              art="summe"
            />
          </>
        ) : (
          <Zeile bez="Fracht" wert={mitFracht ? "—" : "ab Hof, keine"} />
        )}
      </Schritt>

      <Schritt
        nr={6}
        titel="Zahlungsziel"
        quelle={
          <>
            „Zahlungsziel: Finanzierungskosten je nach Zahlungsziel addieren." Gerechnet auf Ware
            und Fracht, kaufmännisch mit 360 Tagen. Steht der Zinssatz auf null, bleibt der
            Schritt ohne Wirkung.
          </>
        }
      >
        <Zeile
          bez={`${formatProzent(p.zinssatz_bp ?? 0)} % p. a.`}
          wert={bezug ? `+${t(bezug.ergebnis.finanzierung_e4)}` : "—"}
        />
      </Schritt>

      {laufzeit && bezug && (
        <Schritt
          nr={7}
          titel="Laufzeit"
          warnung
          quelle={
            <>
              Die Schritte 1–6 gelten für <strong>einen</strong> Liefermonat. Über die Laufzeit
              wechselt nur die Weizennotierung — sie läuft über die Terminkurve
              ({laufzeit.kontrakte.join(", ")}). <strong>Alles andere ist heutiger Stand:</strong>{" "}
              Qualitätsprämie, Diesel, Mahllohn, Nachprodukterlös, Tages- und Zinssätze. Die Mühle
              sichert das Korn ab, nicht die Lohnkosten — ein Risikoaufschlag für die übrigen
              Posten steckt hier <strong>nicht</strong> drin.
            </>
          }
        >
          <Zeile bez={`günstigster von ${laufzeit.anzahl} Monaten`} wert={`${t(laufzeit.guenstigster_e4)} €/t`} />
          <Zeile bez="teuerster Monat" wert={`${t(laufzeit.teuerster_e4)} €/t`} />
          <Zeile
            bez="mengengewichtet über die Abrufmengen"
            wert={`${t(laufzeit.preis_je_t_e4)} €/t`}
            art="summe"
          />
        </Schritt>
      )}

      {laufzeit && (
        <div className="rechnung pf-ergebnis">
          <Zeile bez="Kontraktpreis je Tonne" wert={`${t(laufzeit.preis_je_t_e4)} €`} art="gesamt" />
          <Zeile
            bez="je 100 kg, lose, frachtfrei"
            wert={`${t(laufzeit.preis_je_100kg_e4)} €`}
            art="summe"
          />
        </div>
      )}

      {bezug && !laufzeit && (
        <div className="rechnung pf-ergebnis">
          <Zeile bez="Preis je Tonne" wert={`${t(bezug.ergebnis.preis_je_t_e4)} €`} art="gesamt" />
          <Zeile
            bez="je 100 kg, lose, frachtfrei"
            wert={`${t(bezug.ergebnis.preis_je_100kg_e4)} €`}
            art="summe"
          />
        </div>
      )}

      {bezug && <Empfindlichkeit stamm={stamm} bezug={bezug} />}
    </div>
  );
}

/**
 * Was bewegt den Preis?
 *
 * Der Teil, der aus einer Herleitung ein Verständnis macht. Die drei Zahlen
 * beantworten Fragen, die im Verkaufsgespräch tatsächlich kommen — und zwei
 * davon fallen anders aus, als man vermutet: Der Dieselpreis schlägt nur zu
 * gut einem Viertel auf die Fracht durch, und ein Ausbeutepunkt bewegt fast
 * nichts, weil zugleich der Nachprodukterlös steigt.
 */
function Empfindlichkeit({ stamm, bezug }: { stamm: PreisfindungStamm; bezug: PreisZeile }) {
  const m = bezug.ergebnis.mehl;
  const basis = {
    bestandteile: m.bestandteile,
    mahllohn_e4: m.mahllohn_e4,
    ausbeute_bp: bezug.ausbeute_bp,
    nachprodukt_anteil_bp: bezug.nachprodukt_anteil_bp,
    nachprodukt_preis_e4: stamm.parameter.nachprodukt_preis_e4 ?? 0,
  };

  /*
   * „Korn +10 €/t" hebt JEDE Notierung der Mischung an, nicht nur eine.
   * Bei einem Mischbrotmehl waere „Weizen +10" sonst eine erfundene Zahl:
   * dort haengen nur 30 % am Weizen.
   */
  const korn = berechneMehlAbHof({
    ...basis,
    bestandteile: m.bestandteile.map((b) => ({ ...b, notierung_e4: b.notierung_e4 + 100_000 })),
  }).ab_hof_e4;

  // Sinkt die Ausbeute um einen Punkt, steigt der Nachproduktanteil um
  // denselben — beides gehört zusammen, sonst misst der Vergleich Unsinn.
  const ausbeute =
    bezug.ausbeute_bp > 100
      ? berechneMehlAbHof({
          ...basis,
          ausbeute_bp: bezug.ausbeute_bp - 100,
          nachprodukt_anteil_bp: bezug.nachprodukt_anteil_bp + 100,
        }).ab_hof_e4
      : null;

  // Diesel +10 % trifft nur den Kraftstoffanteil der Tour, nicht die Tour.
  const f = bezug.ergebnis.fracht;
  const diesel = f
    ? roundDiv((f.tour_e4 + roundDiv(f.kraftstoff_e4, 10)) * 1000, f.lademenge_kg) - f.je_tonne_e4
    : null;

  return (
    <section className="pf-schritt pf-empfindlich">
      <h4>Was bewegt den Preis?</h4>
      <div className="rechnung">
        <Zeile
          bez={`${bezug.mischung.length > 1 ? "Alle Sorten" : "Korn"} +10,00 €/t`}
          wert={`${vorzeichen(korn - m.ab_hof_e4)} €/t Mehl`}
        />
        {ausbeute !== null && (
          <Zeile
            bez={`Ausbeute ${formatProzent(bezug.ausbeute_bp)} → ${formatProzent(bezug.ausbeute_bp - 100)} %`}
            wert={`${vorzeichen(ausbeute - m.ab_hof_e4)} €/t Mehl`}
          />
        )}
        {diesel !== null && f && (
          <Zeile
            bez={`Diesel +10 % (${formatProzent(f.dieselanteil_bp)} % der Tour)`}
            wert={`${vorzeichen(diesel)} €/t Mehl`}
          />
        )}
      </div>
      <p className="pf-quelle">
        Das Korn wirkt mit Hebel — jeder Euro schlägt geteilt durch die Ausbeute durch. Ein
        Ausbeutepunkt dagegen kaum, weil gegenläufig der Nachprodukterlös steigt. Und der Diesel
        bewegt nur seinen Anteil an der Tour, nicht die ganze Fracht.
      </p>
    </section>
  );
}
